/**
 * The front door.
 *
 * Not a marketing page — the app's first screen when nobody is signed in. It has
 * two jobs and they are equal: say what Punchboard is to someone who has never
 * heard of it, and get them into an account without sending them anywhere else.
 * The form is on this page for exactly that reason; a separate `/sign-in` route
 * would be one more tap between a person and the thing they came for.
 *
 * The hero is the name taken literally. A punchboard is the die-cut sheet a
 * game's counters arrive in, and the animation punches chits out of one, drops
 * them face down on the table, and turns them all over at once — which is the
 * product's one non-obvious idea, shown rather than claimed. `sheet.ts` owns it.
 *
 * Everything the animation says is also said in words next to it, and the canvas
 * is `aria-hidden`: nobody has to see it to use this page, and nobody who has
 * asked for reduced motion is shown an empty box in its place.
 */

import { useState } from "react";
import type { FormEvent, ReactNode } from "react";

import { PunchSheet } from "./PunchSheet.tsx";
import { EMPTY_FORM, credentialsOf, setField, setMode, validate, type AuthForm } from "./form.ts";
import { signIn, signUp, useAuthSession } from "./session.ts";

import "./signed-out.css";

export function SignedOut(): ReactNode {
  const { notice } = useAuthSession();

  return (
    <main className="pb-door">
      <div className="pb-door__grid">
        <header className="pb-door__mast">
          <div className="pb-door__wordmark">
            Punch<span className="pb-door__wordmark-cut">board</span>
          </div>
          <div className="pb-door__strap">Post-game review, around the table</div>
        </header>

        <div className="pb-door__hero">
          <PunchSheet />
          <div className="pb-door__hero-caption">
            <span className="pb-label">Fig. 1</span>
            <span>
              A sheet punches out. Five chits land face down. They turn over together.
            </span>
          </div>
        </div>

        <section className="pb-door__lede">
          <p>
            You have just finished a game. Before the box is packed and everyone drifts to their
            phones, Punchboard walks the table through it — one aspect at a time, out loud.
          </p>
          <p>
            Everyone records their own rating <strong>blind</strong>. Nothing is visible, no handle
            starts at a default, and nobody has to answer after hearing what the loudest person
            thought. When the last person is done, every rating reveals at once.
          </p>
        </section>

        <Panel notice={notice} />

        <section className="pb-door__steps" aria-labelledby="pb-door-steps-title">
          <h2 id="pb-door-steps-title" className="pb-door__section-title">
            How a review runs
          </h2>
          <ol className="pb-door__track">
            <Step
              index="01"
              title="Talk it through"
              body="One aspect at a time — the ideas, the table talk, how it ended. The conversation is the product. The ratings are what it leaves behind."
            />
            <Step
              index="02"
              title="Rate blind"
              body="Each person answers on their own device, or on the one phone passed around. Nothing is shown until everyone has answered, so nobody anchors on whoever spoke first."
            />
            <Step
              index="03"
              title="Reveal together"
              body="Every rating turns over at once. They are named stops on a scale, not naked numbers, and the overall score is never a bare average of them — disagreement is the interesting part, so it is kept."
            />
          </ol>
        </section>

        <section className="pb-door__sofar" aria-labelledby="pb-door-sofar-title">
          <h2 id="pb-door-sofar-title" className="pb-door__section-title">
            What is here so far
          </h2>
          <p className="pb-door__sofar-note">
            This is v1 and it is still being built. Being straight about the edges:
          </p>
          <dl className="pb-door__ledger">
            <div className="pb-door__ledger-row" data-state="in">
              <dt>Working</dt>
              <dd>
                Your shelf and BoardGameGeek lookup, running a review on one shared phone or with
                guests joining by QR code, blind entry and the reveal, your own aspect sets, and the
                whole app offline — ratings queue on the device and sync when there is a signal.
              </dd>
            </div>
            <div className="pb-door__ledger-row" data-state="out">
              <dt>Not yet</dt>
              <dd>
                There is no password reset — no email is sent by this app at all, so keep your
                password somewhere you will find it. Long-run statistics across your shelf are not
                built yet either.
              </dd>
            </div>
          </dl>
        </section>

        <footer className="pb-door__foot">
          <span className="pb-label">Punchboard v1</span>
          <span className="pb-door__foot-note">
            Scanned a QR code to join someone else's game? That link works without an account — open
            it and pick your name.
          </span>
        </footer>
      </div>
    </main>
  );
}

function Step(props: { index: string; title: string; body: string }): ReactNode {
  return (
    <li className="pb-door__step">
      <span className="pb-door__step-index" aria-hidden="true">
        {props.index}
      </span>
      <div className="pb-door__step-body">
        <h3 className="pb-door__step-title">{props.title}</h3>
        <p>{props.body}</p>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// The way in
// ---------------------------------------------------------------------------

/**
 * Sign in and register share one form and one state record, so switching tabs
 * carries every typed value across — see `form.ts`. Losing a half-typed email to
 * a mis-tapped tab is the kind of small insult that ends a first visit.
 */
function Panel(props: { notice: string | null }): ReactNode {
  const [form, setForm] = useState<AuthForm>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const registering = form.mode === "register";

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    const problem = validate(form);
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    setBusy(true);
    const { email, password, name } = credentialsOf(form);
    const result = registering
      ? await signUp({ email, password, name })
      : await signIn({ email, password });
    // On success the gate swaps this page for the app, so there is nothing to
    // navigate to and nothing to clear — this component is about to be gone.
    if (!result.ok) {
      setError(result.message);
      setBusy(false);
    }
  };

  return (
    <section className="pb-door__panel" aria-labelledby="pb-door-panel-title">
      <div className="pb-door__panel-head">
        <h2 id="pb-door-panel-title" className="pb-door__section-title">
          {registering ? "Make an account" : "Sign in"}
        </h2>
        <p className="pb-door__panel-sub">
          {registering
            ? "Your own shelf, your own aspects, nobody else's ratings in it."
            : "Back to your shelf and your reviews."}
        </p>
      </div>

      {/* Own class rather than the shell's `pb-notice`: that one is defined in
          the session flow's stylesheet, which the front door never loads. */}
      {props.notice ? (
        <div className="pb-door__notice" role="status">
          <span className="pb-door__notice-strong">Signed out</span>
          <span>{props.notice}</span>
        </div>
      ) : null}

      <div className="pb-door__tabs" role="group" aria-label="Sign in or register">
        <button
          type="button"
          className="pb-door__tab"
          data-on={!registering}
          aria-pressed={!registering}
          onClick={() => setForm(setMode(form, "sign-in"))}
        >
          Sign in
        </button>
        <button
          type="button"
          className="pb-door__tab"
          data-on={registering}
          aria-pressed={registering}
          onClick={() => setForm(setMode(form, "register"))}
        >
          Register
        </button>
      </div>

      <form className="pb-door__form" onSubmit={(e) => void submit(e)} noValidate>
        {registering ? (
          <label className="pb-door__field">
            <span className="pb-label">Name at the table</span>
            <input
              className="pb-door__input"
              type="text"
              name="name"
              autoComplete="name"
              value={form.name}
              onChange={(e) => setForm(setField(form, "name", e.target.value))}
              placeholder="How the group knows you"
            />
          </label>
        ) : null}

        <label className="pb-door__field">
          <span className="pb-label">Email</span>
          <input
            className="pb-door__input"
            type="email"
            name="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            value={form.email}
            onChange={(e) => setForm(setField(form, "email", e.target.value))}
          />
        </label>

        <label className="pb-door__field">
          <span className="pb-label">Password</span>
          <input
            className="pb-door__input"
            type="password"
            name="password"
            autoComplete={registering ? "new-password" : "current-password"}
            value={form.password}
            onChange={(e) => setForm(setField(form, "password", e.target.value))}
          />
          {registering ? <span className="pb-door__hint">At least 8 characters.</span> : null}
        </label>

        {/* Announced rather than only coloured: this is the one thing on the
            page a person is waiting on, and it can arrive after they look away. */}
        <div className="pb-door__error" role="alert" aria-live="polite">
          {error ?? ""}
        </div>

        <button className="pb-btn pb-door__submit" data-variant="primary" type="submit" disabled={busy}>
          {busy ? "One moment" : registering ? "Create account and start" : "Sign in"}
        </button>
      </form>
    </section>
  );
}
